import React, { useState } from 'react';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock } from 'lucide-react';
import { useAppDialog } from '@/hooks/useAppDialog';
import { SHIFT_PERIODS, getShiftPeriod } from './ShiftPeriodBadge';
import { ServiceActivityBreakdownPanel } from './ServiceActivityBreakdownPanel';

// Recommended canonical time ranges per shift period (advisory only; SLEEP_OVER is manual).
export const RECOMMENDED_TIMES = {
  MORNING: { start: '06:00', end: '14:00' }, AFTERNOON: { start: '14:00', end: '18:00' },
  EVENING: { start: '18:00', end: '22:00' }, ACTIVE_NIGHT: { start: '22:00', end: '06:00' },
};

/** UC-A-5 Plan tab (UI-06/07): the Participant Service Segments panel is the main Plan content for ALL participant shifts. */
export function ShiftPlanTab({ shift, permissions, onUpdate, onMeta, readOnly }) {
  const [savingPeriod, setSavingPeriod] = useState(false);
  const { dangerConfirm } = useAppDialog();
  const canEdit = (permissions.includes('roster.update') || permissions.includes('roster.manage')) && !readOnly;
  const periodInfo = getShiftPeriod(shift);
  const recommended = shift.shift_period ? RECOMMENDED_TIMES[shift.shift_period] : null;
  const mismatch = recommended && !(shift.start_time === recommended.start && shift.end_time === recommended.end);

  const put = async (body, ok) => {
    setSavingPeriod(true);
    try { await api.put(`/roster/shifts/${shift.id}`, body); toast.success(ok); onUpdate(); }
    catch (e) { notify.error(e, 'SAVE_FAILED'); }
    finally { setSavingPeriod(false); }
  };

  return (
    <div className="space-y-3" data-testid="shift-plan-tab">
      <ServiceActivityBreakdownPanel shift={shift} onChange={onUpdate} onMeta={onMeta} compact readOnly={readOnly} />
      {canEdit && (
        <div className="flex items-center gap-2 text-xs" data-testid="plan-period-row">
          <span className="text-muted-foreground shrink-0">Shift period{periodInfo.auto ? ' (auto)' : ''}</span>
          <Select value={shift.shift_period || 'AUTO'} onValueChange={(v) => put({ shift_period: v === 'AUTO' ? null : v }, v === 'AUTO' ? 'Shift period set to auto' : `Shift period: ${v.replace('_', ' ').toLowerCase()}`)} disabled={savingPeriod}>
            <SelectTrigger className="h-7 text-xs w-40" data-testid="drawer-shift-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="AUTO">Auto-detect</SelectItem>
              {SHIFT_PERIODS.map((p) => <SelectItem key={p} value={p}>{p.replace('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}</SelectItem>)}
            </SelectContent>
          </Select>
          {mismatch && (
            <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-400 text-amber-900 hover:bg-amber-100" disabled={savingPeriod} data-testid="period-apply-recommended-time-btn"
              onClick={async () => {
                const ok = await dangerConfirm({ title: 'Update shift time?', description: `Current ${shift.start_time}–${shift.end_time} → ${recommended.start}–${recommended.end}. This will not affect billing, payroll, invoices, claims, or funding.`, confirmLabel: 'Apply change', cancelLabel: 'Cancel', variant: 'warning', testId: 'shift-period-time-confirm' });
                if (ok) put({ start_time: recommended.start, end_time: recommended.end }, `Shift times updated to ${recommended.start} – ${recommended.end}`);
              }}>
              <Clock className="w-3 h-3 mr-1" /> Apply {recommended.start}–{recommended.end}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default ShiftPlanTab;
